import React, { useState, Fragment, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
// import { ThemeProvider, StyledEngineProvider, makeStyles } from '@mui/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TopActions from '../actions/TopActions';
import Toast from '../actions/Toasts';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import apiService from '../../services/apiServices';
import InputController from '../../controllers/InputController';
import SelectController from '../../controllers/SelectController';
import { workTypeOptions } from '../../utils/workTypeOptions';
import { departmentsForBranch } from '../../utils/departmentBranches';
import { useContractPayRates, applyPayChange } from '../../utils/contractPay';
import { TextareaAutosize } from '@mui/material';
import { Switch } from '@mui/material';
import {
  useParams,
} from 'react-router-dom';


export default function AddWorkerContract() {
  const { t } = useTranslation();
  const toastInitial = { open: false, message: '', variant: '' };
  const [toast, setToast] = useState(toastInitial);
  const [workTypes, setWorkTypes] = useState([{ id: 0, name: "" }]);
  const [departments, setDepartments] = useState([{ id: 0, name: "" }]);
  const [branch, setBranch] = useState([{ id: 0, name: "" }]);
  const [templates, setTemplates] = useState([{ id: 0, name: "" }])
  const [fiscalDefaults, setFiscalDefaults] = useState({ employee: 5, employer: 5, brutoNeto: 1 });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      WithTax: true,
      EmployeeTax: 5,
      EmployerTax: 5,
      TermsOfContract: '',
    },
  });
  const salaryTypes = useMemo(() => [{ id: 0, name: '' }, { id: 1, name: t('worker.monthly') }, { id: 2, name: t('worker.byHour') }], [t]);
  const contractExtension = useMemo(() => [{ id: 0, name: '' }, { id: 1, name: t('worker.monthly') }, { id: 2, name: t('worker.yearly') }], [t]);
  let { id } = useParams();
  const payRates = useContractPayRates();
  const brutoNeto = useMemo(() => [{ id: 0, name: '' }, { id: 1, name: t('worker.bruto') }, { id: 2, name: t('worker.neto') }], [t]);

  const loadWorkTypes = useCallback(async () => {
    let rows = [];
    try {
      const wt = await apiService().get('worktype');
      if (wt?.success) rows = wt.data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load work types', err);
    }
    const options = workTypeOptions(rows);
    setWorkTypes(options);
    if (!watch('WorkType') && options.length) {
      setValue('WorkType', options[0]);
    }
  }, [setValue, watch]);

  const loadFiscalDefaults = useCallback(async () => {
    let defaults = { employee: 5, employer: 5, brutoNeto: 1 };
    try {
      const res = await apiService().get('fiscalPolitics');
      const fp = res?.success && Array.isArray(res.data) && res.data.length ? res.data[0] : null;
      if (fp) {
        defaults = {
          employee: Number(fp.EmployeeTax ?? fp.employeeTax ?? 5),
          employer: Number(fp.EmployerTax ?? fp.employerTax ?? 5),
          brutoNeto: fp.Type ?? fp.type ?? 1,
        };
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load fiscal defaults', err);
    }
    setFiscalDefaults(defaults);
    setValue('WithTax', true);
    setValue('EmployeeTax', defaults.employee);
    setValue('EmployerTax', defaults.employer);
    const defaultBruto = brutoNeto.find((o) => String(o.id) === String(defaults.brutoNeto)) || brutoNeto[0];
    setValue('BrutoNeto', defaultBruto);
  }, [brutoNeto, setValue]);

  useEffect(() => {
    const toLowerKeys = (obj = {}) => Object.keys(obj).reduce((accumulator, key) => {
      accumulator[key.charAt(0).toLowerCase() + key.slice(1, -1) + key.slice(-1).toLocaleLowerCase()] = obj[key];
      return accumulator;
    }, {});

    const fetchTemplates = () => {
      apiService().get('contractsTemplates')
        .then(response => {
          if (response.success) {
            const arr = [];
            const data = response.data;
            data?.forEach(d => {
              arr.push(toLowerKeys(d));
            });
            setTemplates(arr);
          }
        });
    };
    loadWorkTypes();
    loadFiscalDefaults();

    apiService()
      .get('department')
      .then((response) => {
        if (response.success) {
          setDepartments((response.data || []).filter((d) => d.Active !== false));
        } else setToast({ open: true, message: 'Error: ' + response.errorMessage, variant: 'error' });
      });
    apiService()
      .get('branch')
      .then((response) => {
        if (response.success) {
          setBranch((response.data || []).filter((b) => b.Active !== false));
        } else setToast({ open: true, message: 'Error: ' + response.errorMessage, variant: 'error' });
      });

    setValue("WithTax", true)
    fetchTemplates();
  }, [loadFiscalDefaults, loadWorkTypes, setValue]);


  const extractId = (val, fallback) => {
    if (val === undefined || val === null) return fallback ?? null;
    if (typeof val === 'object') return val.id ?? val.Id ?? val.value ?? fallback ?? null;
    return val;
  };

  const onSubmit = (data) => {
    const payload = {
      ...data,
      WorkerId: id,
      ContractType: extractId(data.ContractType),
      WorkType: extractId(data.WorkType),
      SalaryType: extractId(data.SalaryType),
      Department: extractId(data.Department),
      BranchId: extractId(data.BranchId),
      BrutoNeto: extractId(data.BrutoNeto),
      Template: extractId(data.Template),
      TermsOfContract: data.TermsOfContract ?? '',
      createdBy: '',
    };
    // Backend table doesn't have WithTax; remove it from payload
    delete payload.WithTax;

    apiService()
      .post('workerContract', payload)
      .then((response) => {
        console.log(response)
        if (response.success) {
          setToast({
            open: true,
            message: 'U ruajten me sukses!',
            variant: 'success',
          });
          const contractId =
            response?.data?.[0]?.id ||
            response?.data?.id ||
            response?.data?.[0]?.Id ||
            response?.data?.Id;
          if (contractId) {
            window.location.href = `#/workerlist/editworkercontract/${contractId}`;
          } else {
            setToast({
              open: true,
              message: t('worker.idMissingInResponse'),
              variant: 'warning',
            });
          }
        } else {
          setToast({
            open: true,
            message: t('common.errorOccurred'),
            variant: 'error',
          });
        }
      });
  };

  const setTaxes = (isTrue) => {
    setValue('WithTax', isTrue);
    if (!isTrue) {
      setValue('EmployeeTax', 0);
      setValue('EmployerTax', 0);
    }
    else {
      const defaults = fiscalDefaults || {};
      !watch('EmployeeTax') ? setValue('EmployeeTax', defaults.employee ?? 5) : setValue('EmployeeTax', watch('EmployeeTax'));
      !watch('EmployerTax') ? setValue('EmployerTax', defaults.employer ?? 5) : setValue('EmployerTax', watch('EmployerTax'));
    }
  }

  return (
    <div className="">
      <Fragment>
        <Toast {...toast} />
        <TopActions
          newHref="/workerlist/register"
          save={handleSubmit(onSubmit)}
        />

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contract Details
            </Typography>
            <form>
              <Grid container spacing={3}>

                <Grid item xs={12} sm={3} lg={3}>
                  <InputController
                    placeholder="Data e fillimit"
                    error={errors.StartDate}
                    control={control}
                    type="date"
                    register={{ ...register('StartDate') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <InputController
                    placeholder="Data e mbarimit"
                    error={errors.EndDate}
                    control={control}
                    type="date"
                    register={{ ...register('EndDate') }}
                  />
                </Grid>

                <Grid item xs={12} sm={3} lg={3}>
                  <SelectController
                    placeholder="Lloji i punes"
                    error={errors.WorkType}
                    control={control}
                    options={workTypes}
                    name="WorkType"
                    register={{ ...register('WorkType') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <SelectController
                    placeholder="Filiali"
                    error={errors.BranchId}
                    control={control}
                    options={branch}
                    name="BranchId"
                    register={{ ...register('BranchId') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <SelectController
                    placeholder={t('common.department')}
                    error={errors.Department}
                    control={control}
                    options={departmentsForBranch(departments, watch('BranchId')?.Id ?? watch('BranchId')?.id)}
                    name="Department"
                    register={{ ...register('Department') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <SelectController
                    placeholder={t('worker.salaryType')}
                    error={errors.SalaryType}
                    control={control}
                    options={salaryTypes}
                    name="SalaryType"
                    register={{ ...register('SalaryType') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <InputController
                    placeholder={t('worker.baseSalary')}
                    error={errors.BaseSalary}
                    control={control}
                    symbol="€"
                    register={{ ...register('BaseSalary') }}
                    onFieldChange={(e) => applyPayChange(setValue, 'BaseSalary', e?.target?.value, payRates)}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <SelectController
                    placeholder="Bruto/Neto"
                    error={errors.BrutoNeto}
                    control={control}
                    options={brutoNeto}
                    name="BrutoNeto"
                    register={{ ...register('BrutoNeto') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <InputController
                    placeholder={t('worker.hourSalary')}
                    control={control}
                    symbol="€"
                    register={{ ...register('HourSalary') }}
                    onFieldChange={(e) => applyPayChange(setValue, 'HourSalary', e?.target?.value, payRates)}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <InputController
                    placeholder={t('worker.hourSalaryWeekend')}
                    error={errors.HourSalaryWeekend}
                    control={control}
                    symbol="€"
                    register={{ ...register('HourSalaryWeekend') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <InputController
                    placeholder={t('worker.hourSalaryHoliday')}
                    error={errors.HourSalaryHoliday}
                    control={control}
                    symbol="€"
                    register={{ ...register('HourSalaryHoliday') }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Plotësoni pagën bazë <b>ose</b> pagën për orë — tjetra llogaritet vetë: ora = paga ÷ 173,33 orë (LP Neni 20).
                    Fundjava +{payRates.WeekendRate}% dhe festa +{payRates.HolidayRate}% sipas politikës fiskale.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={1} lg={1}>
                  <FormControlLabel
                    control={<Switch
                      error={errors.WithTax}
                      control={control}
                      name="WithTax"
                      register={{ ...register('WithTax') }}
                      onChange={(e) => setTaxes(e.target.checked)}
                      checked={!!watch("WithTax")}
                    />}
                    label={t('worker.contribution')}
                  />
                </Grid>
                {watch('WithTax') ?
                  <>
                    <Grid item xs={12} sm={2} lg={2}>
                      <InputController
                        placeholder={t('worker.workerContribution')}
                        error={errors.EmployeeTax}
                        control={control}
                        symbol="%"
                        value={watch('EmployeeTax') || 5}
                        register={{ ...register('EmployeeTax') }}
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} lg={3}>
                      <InputController
                        placeholder={t('worker.employerContribution')}
                        error={errors.EmployerTax}
                        control={control}
                        symbol="%"
                        value={watch('EmployerTax') || 5}
                        register={{ ...register('EmployerTax') }}
                        type="number"
                      />
                    </Grid>
                  </>
                  : ''}
                <Grid item xs={12} sm={3} lg={3}>
                  <SelectController
                    placeholder={t('worker.contractExtension')}
                    error={errors.ContractExtension}
                    control={control}
                    options={contractExtension}
                    name="ContractType"
                    value=""
                    register={{ ...register('ContractType') }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} lg={3}>
                  <SelectController
                    placeholder={t('worker.contractTemplate')}
                    error={errors.Template}
                    control={control}
                    options={templates}
                    name="Template"
                    value=""
                    register={{ ...register('Template') }}
                  />
                </Grid>

              </Grid>
              <Grid item xs={12} style={{ marginTop: 20 }}>
              <TextareaAutosize
                className='custom-textarea'
                placeholder={t('worker.contractConditions')}
                error={errors.TermsOfContract}
                control={control}
                type="textarea"
                style={{ width: '100%' }}
                minRows={5}
                name="TermsOfContract"
                defaultValue=""
                {...register('TermsOfContract')}
                onChange={(e) => setValue('TermsOfContract', e.target.value)}
              />
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Fragment>
    </div>
  );
}
